import { z } from "zod";

export const providerIdSchema = z.string().regex(/^[0-9a-f]{32}$/u);
const idSchema = providerIdSchema;
const isoDateTimeSchema = z.string().datetime({ offset: true });
const versionNoSchema = z.number().int().min(1).max(2_147_483_647);
const simultaneousBookingCapacitySchema = z.number().int().min(1).max(100);
const minimumNoticeMinutesSchema = z.number().int().min(0).max(99_999_999);
const rateCardAmountMinorSchema = z.number().int().min(1).max(99_999_999);
const postalCodeSchema = z.string().regex(/^[0-9]{5}$/u, "Enter a supported 5-digit ZIP code");
const dryCleanerAddressLineSchema = z.string().trim().min(1).max(200);
const dryCleanerLocalitySchema = z.string().trim().min(1).max(100);
const dryCleanerDeliveryPriceMinorSchema = z.number().int().positive().max(99_999_999).nullable();
const providerTimezoneSchema = z.literal("America/Los_Angeles");
const localTimeSchema = z
  .string()
  .regex(/^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/u, "Enter a time in HH:mm format");
const localDateSchema = z
  .string()
  .regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/u, "Enter a date in YYYY-MM-DD format")
  .refine((value) => {
    if (value.startsWith("0000-")) return false;
    const date = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }, "Enter a real calendar date");

export const providerDayOfWeekSchema = z.enum([
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
]);

const providerAvailabilityTimeWindowShape = {
  startTime: localTimeSchema,
  endTime: localTimeSchema,
} as const;

export const providerAvailabilityTimeWindowSchema = z
  .strictObject(providerAvailabilityTimeWindowShape)
  .refine(({ startTime, endTime }) => startTime < endTime, {
    path: ["endTime"],
    message: "End time must be later than start time",
  });

export const providerWeeklyAvailabilityWindowSchema = z
  .strictObject({
    ...providerAvailabilityTimeWindowShape,
    dayOfWeek: providerDayOfWeekSchema,
  })
  .refine(({ startTime, endTime }) => startTime < endTime, {
    path: ["endTime"],
    message: "End time must be later than start time",
  });

function validateNonOverlappingWindows(
  windows: readonly { startTime: string; endTime: string }[],
  context: z.RefinementCtx,
  pathPrefix: readonly (string | number)[] = [],
): void {
  const ordered = windows
    .map((window, index) => ({ ...window, index }))
    .sort((left, right) =>
      left.startTime === right.startTime
        ? left.endTime.localeCompare(right.endTime)
        : left.startTime.localeCompare(right.startTime),
    );
  for (let index = 1; index < ordered.length; index += 1) {
    const previous = ordered[index - 1];
    const current = ordered[index];
    if (previous !== undefined && current !== undefined && current.startTime < previous.endTime) {
      context.addIssue({
        code: "custom",
        path: [...pathPrefix, current.index, "startTime"],
        message: "Availability windows cannot overlap",
      });
    }
  }
}

const providerWeeklyAvailabilityWindowListSchema = z
  .array(providerWeeklyAvailabilityWindowSchema)
  .max(28)
  .superRefine((windows, context) => {
    for (const dayOfWeek of providerDayOfWeekSchema.options) {
      const dayWindows = windows
        .map((window, index) => ({ ...window, index }))
        .filter((window) => window.dayOfWeek === dayOfWeek);
      if (dayWindows.length > 4) {
        context.addIssue({
          code: "custom",
          path: [dayWindows[4]?.index ?? 0],
          message: "Each day can contain at most 4 availability windows",
        });
      }
      const ordered = dayWindows.sort((left, right) =>
        left.startTime === right.startTime
          ? left.endTime.localeCompare(right.endTime)
          : left.startTime.localeCompare(right.startTime),
      );
      for (let index = 1; index < ordered.length; index += 1) {
        const previous = ordered[index - 1];
        const current = ordered[index];
        if (
          previous !== undefined &&
          current !== undefined &&
          current.startTime < previous.endTime
        ) {
          context.addIssue({
            code: "custom",
            path: [current.index, "startTime"],
            message: "Availability windows on the same day cannot overlap",
          });
        }
      }
    }
  });

export const replaceProviderWeeklyAvailabilityInputSchema = z.strictObject({
  versionNo: versionNoSchema,
  windows: providerWeeklyAvailabilityWindowListSchema,
});

export const providerWeeklyAvailabilityConfigurationSchema = z.strictObject({
  providerId: idSchema,
  versionNo: versionNoSchema,
  timezoneRegion: providerTimezoneSchema,
  windows: providerWeeklyAvailabilityWindowListSchema,
});

export const currentProviderWeeklyAvailabilityResponseSchema = z.strictObject({
  status: z.literal("ok"),
  configuration: providerWeeklyAvailabilityConfigurationSchema,
});

export const replaceProviderWeeklyAvailabilityResponseSchema = z.strictObject({
  status: z.enum(["updated", "existing"]),
  configuration: providerWeeklyAvailabilityConfigurationSchema,
});

const unavailableProviderAvailabilityExceptionSchema = z.strictObject({
  localDate: localDateSchema,
  mode: z.literal("UNAVAILABLE"),
  windows: z.array(providerAvailabilityTimeWindowSchema).length(0),
});

const customProviderAvailabilityExceptionSchema = z
  .strictObject({
    localDate: localDateSchema,
    mode: z.literal("CUSTOM"),
    windows: z.array(providerAvailabilityTimeWindowSchema).min(1).max(4),
  })
  .superRefine(({ windows }, context) => {
    validateNonOverlappingWindows(windows, context, ["windows"]);
  });

export const providerAvailabilityExceptionSchema = z.discriminatedUnion("mode", [
  unavailableProviderAvailabilityExceptionSchema,
  customProviderAvailabilityExceptionSchema,
]);

const providerAvailabilityExceptionListSchema = z
  .array(providerAvailabilityExceptionSchema)
  .max(100)
  .superRefine((exceptions, context) => {
    const seen = new Set<string>();
    exceptions.forEach(({ localDate }, index) => {
      if (seen.has(localDate)) {
        context.addIssue({
          code: "custom",
          path: [index, "localDate"],
          message: "Each date can have only one availability override",
        });
      }
      seen.add(localDate);
    });
  });

export const replaceProviderAvailabilityExceptionsInputSchema = z.strictObject({
  versionNo: versionNoSchema,
  exceptions: providerAvailabilityExceptionListSchema,
});

export const providerAvailabilityExceptionsConfigurationSchema = z.strictObject({
  providerId: idSchema,
  versionNo: versionNoSchema,
  timezoneRegion: providerTimezoneSchema,
  exceptions: providerAvailabilityExceptionListSchema,
});

export const currentProviderAvailabilityExceptionsResponseSchema = z.strictObject({
  status: z.literal("ok"),
  configuration: providerAvailabilityExceptionsConfigurationSchema,
});

export const replaceProviderAvailabilityExceptionsResponseSchema = z.strictObject({
  status: z.enum(["updated", "existing"]),
  configuration: providerAvailabilityExceptionsConfigurationSchema,
});

const uniqueDryCleanerPostalCodes = (
  postalCodes: readonly string[],
  context: z.RefinementCtx,
): void => {
  uniquePostalCodes(postalCodes, context);
};

const dryCleanerPostalCodeListSchema = z
  .array(postalCodeSchema)
  .max(100)
  .superRefine(uniqueDryCleanerPostalCodes);

export const dryCleanerBusinessLocationSchema = z.strictObject({
  streetLine1: dryCleanerAddressLineSchema,
  streetLine2: z.string().trim().min(1).max(200).nullable(),
  locality: dryCleanerLocalitySchema,
  regionCode: z.literal("WA"),
  postalCode: postalCodeSchema,
});

export const replaceDryCleanerConfigurationInputSchema = z.strictObject({
  versionNo: versionNoSchema,
  businessLocation: dryCleanerBusinessLocationSchema,
  deliveryEnabled: z.boolean(),
  deliveryPriceMinor: dryCleanerDeliveryPriceMinorSchema,
  deliveryPostalCodes: dryCleanerPostalCodeListSchema,
});

const cleanerCityCodeSchema = z.string().regex(/^[A-Z][A-Z_]{1,62}$/u);
const cleanerAmountMinorSchema = z.number().int().min(1).max(99_999_999);
const cleanerBasePriceSchema = z
  .strictObject({
    cleaningType: z.enum(["STANDARD", "DEEP"]),
    hourlyAmountMinor: cleanerAmountMinorSchema.nullable(),
    sqftAmountMinor: cleanerAmountMinorSchema.nullable(),
  })
  .refine(
    ({ hourlyAmountMinor, sqftAmountMinor }) =>
      hourlyAmountMinor !== null || sqftAmountMinor !== null,
    {
      message: "Each cleaning type needs an hourly or square-foot amount",
    },
  );
const cleanerPaidAddonPriceSchema = z.strictObject({
  addonCode: z.enum(["FRIDGE_INTERIOR", "OVEN_INTERIOR", "INTERIOR_WINDOWS"]),
  amountMinor: cleanerAmountMinorSchema,
});
const cleanerBillingAddressInputSchema = z.strictObject({
  streetLine1: dryCleanerAddressLineSchema,
  streetLine2: z.string().trim().min(1).max(200).nullable(),
  cityCode: cleanerCityCodeSchema,
  postalCode: postalCodeSchema,
});
const cleanerWorkAreaInputSchema = z.strictObject({
  cityCode: cleanerCityCodeSchema,
  postalCodes: z.array(postalCodeSchema).min(1).max(100),
});

function uniqueCleanerValues(
  values: readonly string[],
  context: z.RefinementCtx,
  path: readonly (string | number)[],
): void {
  const seen = new Set<string>();
  values.forEach((value, index) => {
    if (seen.has(value)) {
      context.addIssue({
        code: "custom",
        path: [...path, index],
        message: "Values must be unique",
      });
    }
    seen.add(value);
  });
}

export const replaceCleanerConfigurationInputSchema = z
  .strictObject({
    versionNo: versionNoSchema,
    billingAddress: cleanerBillingAddressInputSchema,
    workArea: cleanerWorkAreaInputSchema,
    basePrices: z.array(cleanerBasePriceSchema).min(1).max(2),
    paidAddons: z.array(cleanerPaidAddonPriceSchema).max(3),
  })
  .superRefine(({ workArea, basePrices, paidAddons }, context) => {
    uniqueCleanerValues(workArea.postalCodes, context, ["workArea", "postalCodes"]);
    uniqueCleanerValues(
      basePrices.map(({ cleaningType }) => cleaningType),
      context,
      ["basePrices"],
    );
    uniqueCleanerValues(
      paidAddons.map(({ addonCode }) => addonCode),
      context,
      ["paidAddons"],
    );
  });

const cleanerBillingAddressSchema = cleanerBillingAddressInputSchema.extend({
  countryCode: z.literal("US"),
  stateCode: z.literal("WA"),
  countyCode: z.literal("KING"),
  cityName: z.string().trim().min(1).max(100),
  timezoneRegion: z.literal("America/Los_Angeles"),
});
const cleanerWorkAreaSchema = cleanerWorkAreaInputSchema.extend({
  countryCode: z.literal("US"),
  stateCode: z.literal("WA"),
  countyCode: z.literal("KING"),
  cityName: z.string().trim().min(1).max(100),
  timezoneRegion: z.literal("America/Los_Angeles"),
});
export const providerCleanerConfigurationSchema = z
  .strictObject({
    providerId: idSchema,
    versionNo: versionNoSchema,
    billingAddress: cleanerBillingAddressSchema,
    workArea: cleanerWorkAreaSchema,
    basePrices: z.array(cleanerBasePriceSchema).min(1).max(2),
    paidAddons: z.array(cleanerPaidAddonPriceSchema).max(3),
  })
  .superRefine(({ workArea, basePrices, paidAddons }, context) => {
    uniqueCleanerValues(workArea.postalCodes, context, ["workArea", "postalCodes"]);
    uniqueCleanerValues(
      basePrices.map(({ cleaningType }) => cleaningType),
      context,
      ["basePrices"],
    );
    uniqueCleanerValues(
      paidAddons.map(({ addonCode }) => addonCode),
      context,
      ["paidAddons"],
    );
  });
export const currentCleanerConfigurationResponseSchema = z.strictObject({
  status: z.literal("ok"),
  providerVersionNo: versionNoSchema,
  configuration: providerCleanerConfigurationSchema.nullable(),
});
export const replaceCleanerConfigurationResponseSchema = z.strictObject({
  status: z.enum(["updated", "existing"]),
  configuration: providerCleanerConfigurationSchema,
});

export const providerDryCleanerConfigurationSchema =
  replaceDryCleanerConfigurationInputSchema.extend({ providerId: idSchema });

export const currentDryCleanerConfigurationResponseSchema = z.strictObject({
  status: z.literal("ok"),
  providerVersionNo: versionNoSchema,
  configuration: providerDryCleanerConfigurationSchema.nullable(),
});

export const replaceDryCleanerConfigurationResponseSchema = z.strictObject({
  status: z.enum(["updated", "existing"]),
  configuration: providerDryCleanerConfigurationSchema,
});

export const providerServiceInputSchema = z.strictObject({
  serviceId: idSchema,
  minNoticeMinutes: minimumNoticeMinutesSchema,
});

function uniqueServiceIds(
  services: readonly { serviceId: string }[],
  context: z.RefinementCtx,
): void {
  const seen = new Set<string>();
  services.forEach(({ serviceId }, index) => {
    if (seen.has(serviceId)) {
      context.addIssue({
        code: "custom",
        path: [index, "serviceId"],
        message: "Each service can be selected only once",
      });
    }
    seen.add(serviceId);
  });
}

const providerServiceInputListSchema = z
  .array(providerServiceInputSchema)
  .max(100)
  .superRefine(uniqueServiceIds);

export const replaceProviderServicesInputSchema = z.strictObject({
  versionNo: versionNoSchema,
  services: providerServiceInputListSchema,
});

export const providerServiceSchema = providerServiceInputSchema.extend({
  versionNo: versionNoSchema,
});

export const providerServicesConfigurationSchema = z.strictObject({
  providerId: idSchema,
  versionNo: versionNoSchema,
  services: z.array(providerServiceSchema).max(100).superRefine(uniqueServiceIds),
});

export const currentProviderServicesResponseSchema = z.strictObject({
  status: z.literal("ok"),
  configuration: providerServicesConfigurationSchema,
});

export const replaceProviderServicesResponseSchema = z.strictObject({
  status: z.enum(["updated", "existing"]),
  configuration: providerServicesConfigurationSchema,
});

export const providerRateCardStatusSchema = z.enum(["DRAFT", "PUBLISHED", "SUPERSEDED"]);

export const providerRateCardPricingUnitSchema = z.enum([
  "JOB",
  "HOUR",
  "ROOM",
  "BEDROOM",
  "BATHROOM",
  "SQFT",
  "ITEM",
  "POUND",
]);

const providerRateCardInputLineSchema = z.strictObject({
  serviceId: idSchema,
  amountMinor: rateCardAmountMinorSchema,
});

const providerRateCardLineSchema = providerRateCardInputLineSchema.extend({
  pricingUnit: providerRateCardPricingUnitSchema,
});

function uniqueRateCardServiceIds(
  lines: readonly { serviceId: string }[],
  context: z.RefinementCtx,
): void {
  const seen = new Set<string>();
  lines.forEach(({ serviceId }, index) => {
    if (seen.has(serviceId)) {
      context.addIssue({
        code: "custom",
        path: [index, "serviceId"],
        message: "Each service can have only one price",
      });
    }
    seen.add(serviceId);
  });
}

const providerRateCardInputLineListSchema = z
  .array(providerRateCardInputLineSchema)
  .min(1)
  .max(100)
  .superRefine(uniqueRateCardServiceIds);

const providerRateCardLineListSchema = z
  .array(providerRateCardLineSchema)
  .min(1)
  .max(100)
  .superRefine(uniqueRateCardServiceIds);

export const providerRateCardSchema = z
  .strictObject({
    id: idSchema,
    providerId: idSchema,
    versionNo: versionNoSchema,
    status: providerRateCardStatusSchema,
    currencyCode: z.literal("USD"),
    createdAt: isoDateTimeSchema,
    publishedAt: isoDateTimeSchema.nullable(),
    lines: providerRateCardLineListSchema,
  })
  .superRefine(({ status, publishedAt }, context) => {
    if (status === "DRAFT" && publishedAt !== null) {
      context.addIssue({
        code: "custom",
        path: ["publishedAt"],
        message: "Draft rate cards cannot have a publication time",
      });
    }
    if (status === "PUBLISHED" && publishedAt === null) {
      context.addIssue({
        code: "custom",
        path: ["publishedAt"],
        message: "Published rate cards must have a publication time",
      });
    }
  });

export const currentProviderRateCardsResponseSchema = z
  .strictObject({
    status: z.literal("ok"),
    providerId: idSchema,
    providerVersionNo: versionNoSchema,
    rateCards: z.array(providerRateCardSchema).max(1_000),
  })
  .superRefine(({ providerId, rateCards }, context) => {
    const seen = new Set<string>();
    rateCards.forEach((rateCard, index) => {
      if (rateCard.providerId !== providerId) {
        context.addIssue({
          code: "custom",
          path: ["rateCards", index, "providerId"],
          message: "Rate card does not belong to this provider",
        });
      }
      if (seen.has(rateCard.id)) {
        context.addIssue({
          code: "custom",
          path: ["rateCards", index, "id"],
          message: "Rate card IDs must be unique",
        });
      }
      seen.add(rateCard.id);
    });
  });

export const createProviderRateCardInputSchema = z.strictObject({
  providerVersionNo: versionNoSchema,
  currencyCode: z.literal("USD"),
  lines: providerRateCardInputLineListSchema,
});

export const publishProviderRateCardInputSchema = z.strictObject({
  providerVersionNo: versionNoSchema,
  rateCardVersionNo: versionNoSchema,
});

export const createProviderRateCardResponseSchema = z.strictObject({
  status: z.enum(["created", "existing"]),
  providerVersionNo: versionNoSchema,
  rateCard: providerRateCardSchema,
});

export const publishProviderRateCardResponseSchema = z.strictObject({
  status: z.enum(["published", "existing"]),
  providerVersionNo: versionNoSchema,
  rateCard: providerRateCardSchema,
});

export const providerApprovalStatusSchema = z.enum([
  "NOT_SUBMITTED",
  "PENDING",
  "APPROVED",
  "REJECTED",
]);

export const providerApprovalPolicyModeSchema = z.enum(["MANUAL", "AUTO"]);

export const providerApprovalReadinessSchema = z
  .strictObject({
    hasOfferedServices: z.boolean(),
    hasServiceAreas: z.boolean(),
    hasAvailability: z.boolean(),
    hasPublishedRateCard: z.boolean(),
    readyToSubmit: z.boolean(),
  })
  .superRefine((readiness, context) => {
    const expected =
      readiness.hasOfferedServices &&
      readiness.hasServiceAreas &&
      readiness.hasAvailability &&
      readiness.hasPublishedRateCard;
    if (readiness.readyToSubmit !== expected) {
      context.addIssue({
        code: "custom",
        path: ["readyToSubmit"],
        message: "Submission readiness must match the provider requirements",
      });
    }
  });

export const providerApprovalSchema = z
  .strictObject({
    providerId: idSchema,
    providerVersionNo: versionNoSchema,
    verificationStatus: z.enum(["PENDING", "VERIFIED", "REJECTED", "EXPIRED"]),
    applicationId: idSchema.nullable(),
    applicationNo: versionNoSchema.nullable(),
    approvalStatus: providerApprovalStatusSchema,
    applicationVersionNo: versionNoSchema.nullable(),
    policyMode: providerApprovalPolicyModeSchema,
    policyVersionNo: versionNoSchema,
    submittedProviderVersionNo: versionNoSchema.nullable(),
    submittedAt: isoDateTimeSchema.nullable(),
    decidedAt: isoDateTimeSchema.nullable(),
    decisionReasonCode: z
      .enum(["AUTO_POLICY", "MANUAL_REVIEW_APPROVED", "MANUAL_REVIEW_REJECTED"])
      .nullable(),
    readiness: providerApprovalReadinessSchema,
  })
  .superRefine((approval, context) => {
    const applicationFields = [
      approval.applicationId,
      approval.applicationNo,
      approval.applicationVersionNo,
      approval.submittedProviderVersionNo,
      approval.submittedAt,
    ];
    if (
      approval.approvalStatus === "NOT_SUBMITTED" &&
      applicationFields.some((value) => value !== null)
    ) {
      context.addIssue({
        code: "custom",
        path: ["applicationId"],
        message: "An unsubmitted provider cannot have an approval application",
      });
    }
    if (
      approval.approvalStatus !== "NOT_SUBMITTED" &&
      applicationFields.some((value) => value === null)
    ) {
      context.addIssue({
        code: "custom",
        path: ["applicationId"],
        message: "A submitted provider must have a complete approval application",
      });
    }
    if (
      approval.approvalStatus === "PENDING" &&
      (approval.decidedAt !== null || approval.decisionReasonCode !== null)
    ) {
      context.addIssue({
        code: "custom",
        path: ["decidedAt"],
        message: "A pending approval cannot have a decision",
      });
    }
    if (
      approval.approvalStatus === "NOT_SUBMITTED" &&
      (approval.decidedAt !== null || approval.decisionReasonCode !== null)
    ) {
      context.addIssue({
        code: "custom",
        path: ["decidedAt"],
        message: "An unsubmitted provider cannot have a decision",
      });
    }
    if (
      (approval.approvalStatus === "APPROVED" || approval.approvalStatus === "REJECTED") &&
      (approval.decidedAt === null || approval.decisionReasonCode === null)
    ) {
      context.addIssue({
        code: "custom",
        path: ["decidedAt"],
        message: "A decided approval must include its decision",
      });
    }
  });

export const currentProviderApprovalResponseSchema = z.strictObject({
  status: z.literal("ok"),
  approval: providerApprovalSchema,
});

export const submitProviderApprovalInputSchema = z.strictObject({
  providerVersionNo: versionNoSchema,
});

export const submitProviderApprovalResponseSchema = z.strictObject({
  status: z.enum(["submitted", "existing"]),
  approval: providerApprovalSchema,
});

function uniquePostalCodes(postalCodes: readonly string[], context: z.RefinementCtx): void {
  const seen = new Set<string>();
  postalCodes.forEach((postalCode, index) => {
    if (seen.has(postalCode)) {
      context.addIssue({
        code: "custom",
        path: [index],
        message: "Each ZIP code can be selected only once",
      });
    }
    seen.add(postalCode);
  });
}

const providerPostalCodeListSchema = z
  .array(postalCodeSchema)
  .max(100)
  .superRefine(uniquePostalCodes);

export const replaceProviderServiceAreasInputSchema = z.strictObject({
  versionNo: versionNoSchema,
  postalCodes: providerPostalCodeListSchema,
});

export const providerServiceAreasConfigurationSchema = z.strictObject({
  providerId: idSchema,
  versionNo: versionNoSchema,
  postalCodes: providerPostalCodeListSchema,
});

export const supportedProviderPostalCodeSchema = z.strictObject({
  postalCode: postalCodeSchema,
  countryCode: z.literal("US"),
  regionCode: z.literal("WA"),
  localityName: z.string().min(1).max(120),
  timezoneRegion: z.string().min(1).max(64),
});

const supportedProviderPostalCodeListSchema = z
  .array(supportedProviderPostalCodeSchema)
  .min(1)
  .max(1_000)
  .superRefine((supportedPostalCodes, context) => {
    uniquePostalCodes(
      supportedPostalCodes.map(({ postalCode }) => postalCode),
      context,
    );
  });

export const currentProviderServiceAreasResponseSchema = z
  .strictObject({
    status: z.literal("ok"),
    configuration: providerServiceAreasConfigurationSchema,
    supportedPostalCodes: supportedProviderPostalCodeListSchema,
  })
  .superRefine(({ configuration, supportedPostalCodes }, context) => {
    const supported = new Set(supportedPostalCodes.map(({ postalCode }) => postalCode));
    configuration.postalCodes.forEach((postalCode, index) => {
      if (!supported.has(postalCode)) {
        context.addIssue({
          code: "custom",
          path: ["configuration", "postalCodes", index],
          message: "Configured ZIP code is not in the supported catalog",
        });
      }
    });
  });

export const replaceProviderServiceAreasResponseSchema = z.strictObject({
  status: z.enum(["updated", "existing"]),
  configuration: providerServiceAreasConfigurationSchema,
});

export const cleanerRoleStateSchema = z
  .strictObject({
    roleCode: z.literal("CLEANER"),
    databaseActive: z.boolean(),
    tokenActive: z.boolean(),
    tokenRefreshRequired: z.boolean(),
  })
  .superRefine(({ databaseActive, tokenActive, tokenRefreshRequired }, context) => {
    if (tokenRefreshRequired !== (databaseActive !== tokenActive)) {
      context.addIssue({
        code: "custom",
        path: ["tokenRefreshRequired"],
        message: "Token refresh state must match the database and token roles",
      });
    }
  });

export const cleanerRoleResponseSchema = z.strictObject({
  status: z.enum(["accepted", "unchanged"]),
  cleanerRole: cleanerRoleStateSchema,
});

export const independentProviderSchema = z.strictObject({
  id: idSchema,
  partyId: idSchema,
  providerKind: z.literal("INDEPENDENT"),
  publicName: z.string().min(1).max(200),
  bio: z.string().max(4_000).nullable(),
  timezoneRegion: z.string().min(1).max(64),
  defaultCurrency: z.string().regex(/^[A-Z]{3}$/u),
  verificationStatus: z.enum(["PENDING", "VERIFIED", "REJECTED", "EXPIRED"]),
  acceptsDirectBook: z.boolean(),
  simultaneousBookingCapacity: simultaneousBookingCapacitySchema,
  status: z.enum(["ACTIVE", "PAUSED", "SUSPENDED", "CLOSED"]),
  versionNo: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const updateProviderBookingCapacityInputSchema = z.strictObject({
  versionNo: versionNoSchema,
  simultaneousBookingCapacity: simultaneousBookingCapacitySchema,
});

export const updateProviderBookingCapacityResponseSchema = z.strictObject({
  status: z.enum(["updated", "existing"]),
  provider: independentProviderSchema,
});

export const currentProviderResponseSchema = z.strictObject({
  status: z.literal("ok"),
  provider: independentProviderSchema,
});

export const createProviderResponseSchema = z.strictObject({
  status: z.enum(["created", "existing"]),
  provider: independentProviderSchema,
});

export type CleanerRoleState = z.infer<typeof cleanerRoleStateSchema>;
export type CleanerRoleResponse = z.infer<typeof cleanerRoleResponseSchema>;
export type IndependentProvider = z.infer<typeof independentProviderSchema>;
export type UpdateProviderBookingCapacityInput = z.infer<
  typeof updateProviderBookingCapacityInputSchema
>;
export type DryCleanerBusinessLocation = z.infer<typeof dryCleanerBusinessLocationSchema>;
export type ReplaceCleanerConfigurationInput = z.infer<
  typeof replaceCleanerConfigurationInputSchema
>;
export type ProviderCleanerConfiguration = z.infer<typeof providerCleanerConfigurationSchema>;
export type ProviderCleanerConfigurationRead = z.infer<
  typeof currentCleanerConfigurationResponseSchema
>;
export type ReplaceDryCleanerConfigurationInput = z.infer<
  typeof replaceDryCleanerConfigurationInputSchema
>;
export type ProviderDryCleanerConfiguration = z.infer<typeof providerDryCleanerConfigurationSchema>;
export type ProviderDryCleanerConfigurationRead = z.infer<
  typeof currentDryCleanerConfigurationResponseSchema
>;
export type ProviderServiceInput = z.infer<typeof providerServiceInputSchema>;
export type ReplaceProviderServicesInput = z.infer<typeof replaceProviderServicesInputSchema>;
export type ProviderService = z.infer<typeof providerServiceSchema>;
export type ProviderServicesConfiguration = z.infer<typeof providerServicesConfigurationSchema>;
export type ProviderRateCardStatus = z.infer<typeof providerRateCardStatusSchema>;
export type ProviderRateCardPricingUnit = z.infer<typeof providerRateCardPricingUnitSchema>;
export type ProviderRateCard = z.infer<typeof providerRateCardSchema>;
export type ProviderRateCardsRead = z.infer<typeof currentProviderRateCardsResponseSchema>;
export type CreateProviderRateCardInput = z.infer<typeof createProviderRateCardInputSchema>;
export type PublishProviderRateCardInput = z.infer<typeof publishProviderRateCardInputSchema>;
export type CreateProviderRateCardResponse = z.infer<typeof createProviderRateCardResponseSchema>;
export type PublishProviderRateCardResponse = z.infer<typeof publishProviderRateCardResponseSchema>;
export type ProviderApprovalStatus = z.infer<typeof providerApprovalStatusSchema>;
export type ProviderApprovalPolicyMode = z.infer<typeof providerApprovalPolicyModeSchema>;
export type ProviderApprovalReadiness = z.infer<typeof providerApprovalReadinessSchema>;
export type ProviderApproval = z.infer<typeof providerApprovalSchema>;
export type SubmitProviderApprovalInput = z.infer<typeof submitProviderApprovalInputSchema>;
export type SubmitProviderApprovalResponse = z.infer<typeof submitProviderApprovalResponseSchema>;
export type ReplaceProviderServiceAreasInput = z.infer<
  typeof replaceProviderServiceAreasInputSchema
>;
export type ProviderServiceAreasConfiguration = z.infer<
  typeof providerServiceAreasConfigurationSchema
>;
export type SupportedProviderPostalCode = z.infer<typeof supportedProviderPostalCodeSchema>;
export type ProviderDayOfWeek = z.infer<typeof providerDayOfWeekSchema>;
export type ProviderAvailabilityTimeWindow = z.infer<typeof providerAvailabilityTimeWindowSchema>;
export type ProviderWeeklyAvailabilityWindow = z.infer<
  typeof providerWeeklyAvailabilityWindowSchema
>;
export type ReplaceProviderWeeklyAvailabilityInput = z.infer<
  typeof replaceProviderWeeklyAvailabilityInputSchema
>;
export type ProviderWeeklyAvailabilityConfiguration = z.infer<
  typeof providerWeeklyAvailabilityConfigurationSchema
>;
export type ProviderAvailabilityException = z.infer<typeof providerAvailabilityExceptionSchema>;
export type ReplaceProviderAvailabilityExceptionsInput = z.infer<
  typeof replaceProviderAvailabilityExceptionsInputSchema
>;
export type ProviderAvailabilityExceptionsConfiguration = z.infer<
  typeof providerAvailabilityExceptionsConfigurationSchema
>;
export type ProviderServiceAreasRead = Pick<
  z.infer<typeof currentProviderServiceAreasResponseSchema>,
  "configuration" | "supportedPostalCodes"
>;
